import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { API_BASE_CRM_URL } from "../../Config/Config";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const UploadTasksByExcel = () => {
  const [sessionUserData, setSessionUserData] = useState([]);
  const [projectsData, setProjectsData] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitBtnLoading, setSubmitBtnLoading] = useState(false);
  const [uploadFileName, setUploadFileName] = useState("");
  const [parsedTasks, setParsedTasks] = useState([]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const userDetails = sessionStorage.getItem("userDetails");
    if (userDetails) {
      setSessionUserData(JSON.parse(userDetails));
    }
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_BASE_CRM_URL}GetProjects`);
        const data = await response.json();
        if (data.Status) {
          setProjectsData(data.ResultData);
        } else {
          setProjectsData([]);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  const projectOptions = projectsData.map((project) => ({
    value: project.id,
    label: project.name,
  }));

  const handleProjectChange = (selectedOption) => {
    setSelectedProject(selectedOption);
  };

  const handleEditImageClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setErrorMessage("");

    if (file) {
      setUploadFileName(file.name);

      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];

      if (!validTypes.includes(file.type)) {
        setErrorMessage("Invalid file type. Please use .xlsx, .xls, or .csv.");
        return;
      }

      if (file.name.includes(" ")) {
        setErrorMessage("File name contains spaces. Please remove spaces.");
        return;
      }

      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets["Task Template"];

        if (!sheet) {
          setErrorMessage("Sheet named 'Task Template' not found.");
          return;
        }

        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const filteredData = jsonData.filter((row) =>
          row["Subject"]?.toString().trim(),
        );
        setParsedTasks(filteredData);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleDownloadTemplate = (e) => {
    e.preventDefault();
    const link = document.createElement("a");
    link.href = `${API_BASE_CRM_URL}DownloadTemplate`;
    link.download = "Tasks.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!selectedProject) {
      setErrorMessage("Please select a project.");
      return;
    }

    if (!selectedFile) {
      setErrorMessage("Please select a file.");
      return;
    }

    setSubmitBtnLoading(true);
    setErrorMessage("");

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("projectid", selectedProject.value);

    try {
      const response = await fetch(`${API_BASE_CRM_URL}UploadTasks`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      setSubmitBtnLoading(false);

      if (result.status) {
        Swal.fire("Success", result.message, "success").then(() => {
          setSelectedFile(null);
          setUploadFileName("");
          setSelectedProject(null);
          setParsedTasks([]);
          window.location.reload();
        });
      } else {
        Swal.fire("Error", result.error || "Upload failed", "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setSubmitBtnLoading(false);
      Swal.fire("Error", "Something went wrong during upload.", "error");
    }
  };

  return (
    <div>
      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvasRightUploadExcel"
        aria-labelledby="offcanvasRightLabel"
        style={{ minWidth: "40%", maxWidth: "40%" }}
      >
        <div className="offcanvas-header">
          <h4 id="offcanvasRightLabel">Upload Tasks</h4>
          <div className="d-flex ms-auto align-items-center">
            <p className="mb-0 me-2">Download Template</p>
            <button
              className="btn"
              title="Download template"
              onClick={handleDownloadTemplate}
            >
              <i className="fa-solid fa-download text-danger fs-4"></i>
            </button>
            <button
              type="button"
              className="btn-close ms-2"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            />
          </div>
        </div>

        {errorMessage && <p className="text-danger ms-3">{errorMessage}</p>}

        <hr />

        <div className="offcanvas-body">
          <form>
            <div className="row">
              <div className="col-6 mb-3">
                <label className="form-label">
                  Project<span className="text-danger">*</span>
                </label>
                <Select
                  value={selectedProject}
                  onChange={handleProjectChange}
                  options={projectOptions}
                  className="basic-single"
                  isClearable
                />
              </div>

              <div className="col-6 text-center">
                <div className="upload" onClick={handleEditImageClick}>
                  <img
                    src="assets/img/icons/drag-drop.svg"
                    alt="Upload"
                    style={{ width: "3rem", cursor: "pointer" }}
                  />
                  <p>
                    Choose <a href="#">file to upload</a>
                  </p>
                  <h6 className="text-primary">{uploadFileName}</h6>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileChange}
                  disabled={submitBtnLoading}
                />
              </div>
            </div>

            {parsedTasks.length > 0 && (
              <div className="mt-4">
                <h6 className="text-success">
                  ✅ Uploaded <strong>{parsedTasks.length}</strong> task
                  {parsedTasks.length > 1 ? "s" : ""}.
                </h6>
                <div className="table-responsive">
                  <table className="table">
                    <thead className="table-light">
                      <tr>
                        <th>Subject</th>
                        <th>Staff Name</th>
                        <th>Description</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Hourly Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedTasks.map((task, idx) => (
                        <tr key={idx}>
                          <td>{task["Subject"]}</td>
                          <td>{task["Staff"]}</td>
                          <td>{task["Description"]}</td>
                          <td>{task["Start Date"]}</td>
                          <td>{task["End Date"]}</td>
                          <td>{task["Hourly Rate"]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-4 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-secondary me-2"
                data-bs-dismiss="offcanvas"
                disabled={submitBtnLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={submitBtnLoading}
              >
                {submitBtnLoading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadTasksByExcel;
